import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, LogOut, Lock, RefreshCw, Trash2, MessageSquare, Mail, Pencil, Check, X, Copy, Send, Filter } from "lucide-react";
import { API, PUBLIC_SITE_URL } from "../config/site";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "toured", label: "Toured" },
  { value: "offer", label: "Offer" },
];

const STATUS_STYLES = {
  new: "bg-sand text-charcoal",
  contacted: "bg-teal/10 text-teal",
  toured: "bg-teal text-ivory",
  offer: "bg-charcoal text-ivory",
};

const LeadsPage = () => {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("leads_passcode") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [gateCode, setGateCode] = useState("");
  const [codeDraft, setCodeDraft] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [copied, setCopied] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");

  const codeGroups = [...new Set(leads.map((l) => l.access_code || ""))].sort();
  const visibleLeads = groupFilter === "all" ? leads : leads.filter((l) => (l.access_code || "") === groupFilter);

  const inviteMessage = `Aloha! Here's the private page I mentioned with details on Kali'u at Ala Moana: ${PUBLIC_SITE_URL} — your access code is ${gateCode}. Text me with any questions! — Marlene`;

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteMessage);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = inviteMessage;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchLeads = useCallback(async (code) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "x-leads-passcode": code } });
      setLeads(res.data);
      setNoteDrafts({});
      setAuthed(true);
      setError("");
      try {
        const gc = await axios.get(`${API}/gate/code`, { headers: { "x-leads-passcode": code } });
        setGateCode(gc.data.code || "");
      } catch {}
    } catch {
      setAuthed(false);
      setError("Incorrect passcode.");
      sessionStorage.removeItem("leads_passcode");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (passcode) fetchLeads(passcode);
  }, [passcode, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/verify`, { passcode: input });
      sessionStorage.setItem("leads_passcode", input);
      setPasscode(input);
    } catch {
      setError("Incorrect passcode.");
      setLoading(false);
    }
  };

  const patchLead = async (id, fields) => {
    const res = await axios.patch(`${API}/leads/${id}`, fields, { headers: { "x-leads-passcode": passcode } });
    setLeads((prev) => prev.map((l) => (l.id === id ? res.data : l)));
  };

  const saveNote = async (lead) => {
    const draft = noteDrafts[lead.id];
    if (draft === undefined || draft === lead.note) return;
    await patchLead(lead.id, { note: draft });
  };

  const saveGateCode = async () => {
    setCodeError("");
    try {
      const res = await axios.put(`${API}/gate/code`, { code: codeDraft }, { headers: { "x-leads-passcode": passcode } });
      setGateCode(res.data.code);
      setCodeDraft(null);
    } catch (err) {
      setCodeError(err.response?.data?.detail || "Could not update the code.");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete "${lead.name}" from the buyer list? This cannot be undone.`)) return;
    await axios.delete(`${API}/leads/${lead.id}`, { headers: { "x-leads-passcode": passcode } });
    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/leads/export`, {
      headers: { "x-leads-passcode": passcode },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "buyer-list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem("leads_passcode");
    setPasscode("");
    setAuthed(false);
    setLeads([]);
    setInput("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center px-6" data-testid="leads-login-screen">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm border border-line bg-white p-10"
          data-testid="leads-login-form"
        >
          <Lock size={20} strokeWidth={1.5} className="text-teal" />
          <h1 className="font-serif text-2xl font-light text-charcoal mt-5">Buyer List — Agent Access</h1>
          <p className="text-sm text-stone2 mt-2">Enter your passcode to view submissions.</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Passcode"
            className="w-full mt-6 bg-transparent border-b border-line focus:border-teal outline-none py-3 text-charcoal placeholder:text-stone2/50 rounded-none"
            data-testid="leads-passcode-input"
          />
          {error && <p className="text-sm text-red-700 mt-3" data-testid="leads-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-teal text-ivory py-3.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-teal-deep transition-colors disabled:opacity-60"
            data-testid="leads-login-submit"
          >
            {loading ? "Checking..." : "View Leads"}
          </button>
          <a href="/" className="block text-center text-[10px] tracking-[0.2em] uppercase text-stone2 mt-6 hover:text-teal transition-colors" data-testid="leads-back-home-link">
            Back to site
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="leads-dashboard">
      <header className="border-b border-line bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl font-light text-charcoal" data-testid="leads-dashboard-title">Kali&rsquo;u Buyer List</h1>
            <p className="text-xs text-stone2 mt-1" data-testid="leads-count">{leads.length} submission{leads.length === 1 ? "" : "s"}</p>
            <div className="flex items-center gap-2 mt-2" data-testid="gate-code-widget">
              <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-teal">Visitor access code:</span>
              {codeDraft === null ? (
                <>
                  <span className="text-sm font-medium text-charcoal" data-testid="gate-code-display">{gateCode || "—"}</span>
                  <button onClick={() => { setCodeDraft(gateCode); setCodeError(""); }} className="text-stone2 hover:text-teal transition-colors" title="Change access code" data-testid="gate-code-edit-button">
                    <Pencil size={13} strokeWidth={1.5} />
                  </button>
                </>
              ) : (
                <>
                  <input
                    value={codeDraft}
                    onChange={(e) => setCodeDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveGateCode()}
                    className="border-b border-teal bg-transparent outline-none text-sm text-charcoal w-36 py-0.5 rounded-none"
                    autoFocus
                    data-testid="gate-code-edit-input"
                  />
                  <button onClick={saveGateCode} className="text-teal hover:text-teal-deep transition-colors" title="Save code" data-testid="gate-code-save-button">
                    <Check size={15} strokeWidth={2} />
                  </button>
                  <button onClick={() => { setCodeDraft(null); setCodeError(""); }} className="text-stone2 hover:text-red-700 transition-colors" title="Cancel" data-testid="gate-code-cancel-button">
                    <X size={15} strokeWidth={2} />
                  </button>
                </>
              )}
            </div>
            {codeError && <p className="text-xs text-red-700 mt-1" data-testid="gate-code-error">{codeError}</p>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchLeads(passcode)} className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold text-charcoal hover:bg-sand transition-colors" data-testid="leads-refresh-button">
              <RefreshCw size={14} strokeWidth={1.5} /> Refresh
            </button>
            <button onClick={exportCsv} className="flex items-center gap-2 bg-teal text-ivory px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-teal-deep transition-colors" data-testid="leads-export-csv">
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
            <button onClick={logout} className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold text-stone2 hover:bg-sand transition-colors" data-testid="leads-logout-button">
              <LogOut size={14} strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-line bg-sand/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="invite-share-card">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-teal">Invite a buyer</p>
            <p className="text-sm text-charcoal/80 mt-1.5 max-w-3xl leading-relaxed" data-testid="invite-message-preview">{inviteMessage}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={copyInvite} className="flex items-center gap-2 border border-teal text-teal px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-teal hover:text-ivory transition-colors" data-testid="invite-copy-button">
              {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={1.5} />} {copied ? "Copied" : "Copy message"}
            </button>
            <a href={`sms:?&body=${encodeURIComponent(inviteMessage)}`} className="flex items-center gap-2 bg-teal text-ivory px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-teal-deep transition-colors" data-testid="invite-sms-button">
              <Send size={14} strokeWidth={1.5} /> Send by text
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {leads.length > 0 && codeGroups.length > 1 && (
          <div className="flex items-center gap-3 mb-6" data-testid="group-filter-bar">
            <Filter size={14} strokeWidth={1.5} className="text-teal" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-teal">Invite group</span>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="border border-line bg-white px-3 py-2 text-xs tracking-[0.1em] uppercase font-semibold text-charcoal outline-none cursor-pointer rounded-none"
              data-testid="group-filter-select"
            >
              <option value="all">All groups ({leads.length})</option>
              {codeGroups.map((c) => (
                <option key={c || "none"} value={c}>
                  {(c || "No code")} ({leads.filter((l) => (l.access_code || "") === c).length})
                </option>
              ))}
            </select>
            {groupFilter !== "all" && (
              <button onClick={() => setGroupFilter("all")} className="text-xs text-stone2 hover:text-teal transition-colors underline underline-offset-2" data-testid="group-filter-clear">
                Clear filter
              </button>
            )}
          </div>
        )}
        {leads.length === 0 ? (
          <p className="text-stone2 text-sm py-20 text-center" data-testid="leads-empty-state">No submissions yet. New signups will appear here, newest first.</p>
        ) : visibleLeads.length === 0 ? (
          <p className="text-stone2 text-sm py-20 text-center" data-testid="leads-filter-empty-state">No submissions in this group yet.</p>
        ) : (
          <div className="overflow-x-auto border border-line bg-white" data-testid="leads-table-wrapper">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-line text-left">
                  {["Name", "Email", "Phone", "Interest", "Timeline", "Budget", "Tour", "Code", "Status", "Note", "Date", "Reach out", ""].map((h, hi) => (
                    <th key={hi} className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-teal whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleLeads.map((l, i) => (
                  <tr key={l.id} className="border-b border-line last:border-0 hover:bg-sand/60 transition-colors" data-testid={`lead-row-${i}`}>
                    <td className="px-5 py-4 font-medium text-charcoal whitespace-nowrap">{l.name}</td>
                    <td className="px-5 py-4 text-stone2">{l.email}</td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap">{l.phone}</td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap">{l.interest}</td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap">{l.timeline}</td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap">{l.price_range || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {l.tour ? (
                        <span className="bg-teal text-ivory text-[10px] tracking-[0.15em] uppercase font-semibold px-2.5 py-1" data-testid={`lead-tour-badge-${i}`}>Tour</span>
                      ) : (
                        <span className="text-stone2/40">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap" data-testid={`lead-code-${i}`}>{l.access_code || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <select
                        value={l.status || "new"}
                        onChange={(e) => patchLead(l.id, { status: e.target.value })}
                        className={`text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-1.5 border border-line outline-none cursor-pointer rounded-none ${STATUS_STYLES[l.status || "new"]}`}
                        data-testid={`lead-status-select-${i}`}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 min-w-[200px]">
                      <input
                        type="text"
                        value={noteDrafts[l.id] !== undefined ? noteDrafts[l.id] : l.note || ""}
                        onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [l.id]: e.target.value }))}
                        onBlur={() => saveNote(l)}
                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                        placeholder="Add a private note…"
                        className="w-full bg-transparent border-b border-transparent focus:border-teal outline-none py-1 text-charcoal text-sm placeholder:text-stone2/40 rounded-none"
                        data-testid={`lead-note-input-${i}`}
                      />
                    </td>
                    <td className="px-5 py-4 text-stone2 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <a
                          href={`sms:${l.phone.replace(/[^+\d]/g, "")}`}
                          className="flex items-center gap-1.5 text-teal hover:text-teal-deep transition-colors text-xs font-semibold"
                          title="Text this buyer"
                          data-testid={`lead-sms-link-${i}`}
                        >
                          <MessageSquare size={14} strokeWidth={1.5} /> Text
                        </a>
                        <a
                          href={`mailto:${l.email}`}
                          className="flex items-center gap-1.5 text-teal hover:text-teal-deep transition-colors text-xs font-semibold"
                          title="Email this buyer"
                          data-testid={`lead-email-link-${i}`}
                        >
                          <Mail size={14} strokeWidth={1.5} /> Email
                        </a>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteLead(l)}
                        className="text-stone2/50 hover:text-red-700 transition-colors"
                        title="Delete lead"
                        data-testid={`lead-delete-button-${i}`}
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-stone2/70 mt-4" data-testid="leads-digest-note">
          A weekly digest of new signups and tour requests is emailed to you every Monday at 8:00 AM Hawaii time.
        </p>
      </main>
    </div>
  );
};

export default LeadsPage;
